import { useState, useRef, useEffect, useContext } from 'react';
import { Form, message } from 'antd';
import ReCAPTCHA from 'react-google-recaptcha';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import Modal from './Modal';
import Utf8 from 'crypto-js/enc-utf8';
import Base64 from 'crypto-js/enc-base64';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/router';
import styled from './register.module.scss';
import { LoginContext } from './index';
import Button from '@/components/UI/Button';
import { useGlobal } from '@/utils/useGlobal';
import SocialLoginLinks from '@/components/SocailLoginLinks';
import { checkAccountIsExist, checkHumanMachine, createSubscribe, createSubscribeCommunity, getVerifyCode, toRegister, toRegisterNew } from '@/service';
import Input from '@/components/UI/Input';
import CountDown from '@/components/CountDown';
import Checkbox from '@/components/UI/Checkbox';
import { getKaamelPlan } from '@/utils/kaamelTest';
import { subscribeCookie } from '@/utils/cookies/subscribeCookie';
import { signupCookie } from '@/utils/cookies/signupCookie';
import { onLogin } from '@/utils/cookie';
import { langeMap } from '@/utils/locals';
import { setLocalStorage } from '@/utils/localStorage';
import { LOGIN_MAP, SUBSCRIBE_MAP, FORUM_APPID_MAP } from '@/constants/forum';
import { ENGLISH_LANG, i18nMap, newVersionRegion, rpcMap } from '@/constants';
import { EFAkIn, EFAkout } from '@/components/UI/svg/My';

interface RegisterType {
    visible: boolean;
    onCancel: () => void;
    /** 登录成功后的回调，如果传入则不会跳转页面 */
    onSuccess?: () => void;
}

message.config({
    duration: 10,
    top: 150,
    getContainer: () => document.body,
    maxCount: 3,
});

const Register = ({ visible, onCancel, onSuccess }: RegisterType) => {
    const recaptchaRef = useRef();
    const context = useContext(LoginContext);
    const SECRET_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_REGISTER_SECRET_KEY;
    const { useLang, Link, gtm } = useGlobal();
    const { t } = useLang('shared.global_login');
    const [inputClass, SetInputClass] = useState({
        activeFirst: '',
        activeSecond: '',
        activeThird: '',
        name: '',
    });
    const [loading, setLoading] = useState(false);
    const [isShowTip, setIsShowTip] = useState(false);
    const [allValidate, setAllValidate] = useState({
        email: false,
        pwd: false,
        confirmPassword: false,
    });
    const [step, setStep] = useState({ first: true, second: false, third: false });
    const [emailValidating, setEmailValidating] = useState<any>(''); // 'success' 'warning' 'error' 'validating'
    const [recaptchaResponse, setRecaptchaResponse] = useState('');
    const [errorTip, setErrorTip] = useState('');
    const [isErrorTip, setIsErrorTip] = useState(false);
    const [kaamelInfo, setKaamelInfo] = useState<any>(null);
    const router = useRouter();
    const { locale = 'us', query } = router;
    const form = Form.useForm()[0];
    const emailCodeForm = Form.useForm()[0];
    const [emailCodeInputStatus, setEmailCodeInputStatus] = useState(0);

    const [resendCode, setResendCode] = useState(60);
    const [formData, setFormData] = useState<Record<string, any>>({});
    const isNewVersionRegion = newVersionRegion(locale);
    const [humanCheck, setHumanCheck] = useState(false);
    const [email, setEmail] = useState('');
    const { _source } = query;

    const hl = rpcMap[ENGLISH_LANG.includes(locale) ? 'en' : locale];
    const isSelected = ['us', 'ca', 'au', 'jp'].includes(locale);
    const redirect = typeof window !== 'undefined' ? window.location.href : router.asPath;

    const handleSwitchToRegister = (btnName: string) => {
        try {
            localStorage.setItem('from_page_login', 'Popup_password-free login/registration');
            gtm.push({
                event: 'uaEvent',
                event_name: 'click_register_switch',
                button_name: btnName,
                signup_type: 'password-free login/registration',
                next_page_name: 'password-free login/registration',
                next_page_type: 'password-free login/registration ',
            });
            context?.setType('passwordless');
        } catch (err) {
            console.log('err:', err);
        }
    };

    const pwdRegx = isNewVersionRegion
        ? /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+{}\[\]:;"'<>,.?\/\\|-])\S{15,32}$/
        : /^.*(?=.*[0-9])(?=.*[A-Za-z])(?=^.{6,32}$).*$/;

    const validateEmail = () => {
        return {
            validator: async (_: any, value: any) => {
                const regx = /^[A-Za-z\d]+([.%~`$^*&(){}<>?"+=!_\\-]*[A-Za-z\d-])*@[a-zA-Z\d-]{1,30}\.[a-zA-Z\d.%#~`$^*&(){}<>?"+=!_\\-]{1,30}$/;
                if (!value) {
                    setEmailValidating('error');
                    setAllValidate({
                        ...allValidate,
                        ...{ email: false },
                    });
                    return Promise.reject(t('site_login_tip_email_empty'));
                }

                if (!regx.test(value)) {
                    setEmailValidating('error');
                    setAllValidate({
                        ...allValidate,
                        ...{ email: false },
                    });
                    return Promise.reject(t('site_login_tip_email_format_incorrect'));
                }
                setEmailValidating('validating');
                try {
                    const res: any = await checkAccountIsExist({ email: value });

                    if (res.code === '0') {
                        setEmailValidating('error');
                        setAllValidate({
                            ...allValidate,
                            ...{ email: false },
                        });
                        return Promise.reject(t('site_login_tip_email_be_registered'));
                    } else if (res.code === '1003') {
                        setAllValidate({
                            ...allValidate,
                            ...{ email: true },
                        });
                        setEmail(value);
                        setEmailValidating('success');
                    }
                    return Promise.resolve();
                } catch (err) {
                    setEmailValidating('');
                    setAllValidate({
                        ...allValidate,
                        ...{ email: false },
                    });
                }
            },
        };
    };

    const validatePassword = () => {
        return {
            validator(_: any, value: any) {
                // const regx = isNewVersionRegion
                //     ? /^.*(?=.*[0-9])(?=.*[A-Za-z])(?=.*[^0-9A-Za-z\f\n\r\t\v])(?=^[0-9A-Za-z\S]{15,32}$).*$/
                //     : /^.*(?=.*[0-9])(?=.*[A-Za-z])(?=^.{6,32}$).*$/;
                if (!value) {
                    setAllValidate({
                        ...allValidate,
                        pwd: false,
                    });
                    return Promise.reject(t('site_login_tip_password_empty'));
                }
                if (!value || pwdRegx.test(value)) {
                    setAllValidate({
                        ...allValidate,
                        pwd: true,
                    });
                    return Promise.resolve();
                }
                setAllValidate({
                    ...allValidate,
                    pwd: false,
                });
                return Promise.reject(t('site_login_tip_password_format_wrong'));
            },
        };
    };

    const onBlurInput = (event: any, value: any) => {
        if (value === 'pwd') {
            setIsShowTip(false);
        }
        if (!event.target.value) {
            if (value == 'email') {
                SetInputClass({
                    activeFirst: '',
                    activeSecond: inputClass.activeSecond,
                    activeThird: inputClass.activeThird,
                    name: value,
                });
            } else if (value == 'pwd') {
                SetInputClass({
                    activeFirst: inputClass.activeFirst,
                    activeSecond: '',
                    activeThird: inputClass.activeThird,
                    name: value,
                });
            } else if (value == 'conpwd') {
                SetInputClass({
                    activeFirst: inputClass.activeFirst,
                    activeSecond: inputClass.activeSecond,
                    activeThird: '',
                    name: value,
                });
            }
        }
    };

    const onFocusInput = (event: any, value: any) => {
        if (value === 'pwd') {
            const [infos] = form.getFieldsError(['password']);
            const { errors } = infos;

            if (!errors.length) {
                setIsShowTip(true);
            }
        }
        if (!event.target.value) {
            if (value == 'email') {
                SetInputClass({
                    activeFirst: 'active',
                    activeSecond: inputClass.activeSecond,
                    activeThird: inputClass.activeThird,
                    name: value,
                });
            } else if (value == 'pwd') {
                SetInputClass({
                    activeFirst: inputClass.activeFirst,
                    activeSecond: 'active',
                    activeThird: inputClass.activeThird,
                    name: value,
                });
            } else if (value == 'conpwd') {
                SetInputClass({
                    activeFirst: inputClass.activeFirst,
                    activeSecond: inputClass.activeSecond,
                    activeThird: 'active',
                    name: value,
                });
            }
        }
    };

    const handleSubmit = async (form1Data?: any) => {
        const recaptchaRes = humanCheck ? { recaptchaResponse } : {};
        const { inviteCode } = query;

        const values = emailCodeForm.getFieldsValue();
        const { email, password, confirmPassword, remember, subscribe } = form1Data || formData;

        gtm.push({
            event: 'uaEvent',
            event_name: 'click_sign_up',
            button_name: 'Popup_register',
            signup_type: 'password',
            source_channel: 'Website',
            from_page: localStorage.getItem('from_page_login') || 'direct',
        });

        const params: any = {
            source: LOGIN_MAP[_source as string] || 'ECOFLOW_WEB',
            email: email,
            userType: 'ECOFLOW', //用户类型
            scene: LOGIN_MAP[_source as string] || 'ECOFLOW_WEB', //登陆场景
            password: Base64.stringify(Utf8.parse(password)),
            confirmPassword: Base64.stringify(Utf8.parse(confirmPassword)),
            isAgree: remember,
            ...recaptchaRes,
        };

        if (isNewVersionRegion) {
            Object.assign(params, { code: values.code });
        }
        if (inviteCode) {
            Object.assign(params, { inviteCode2: inviteCode });
        }

        setLoading(true);

        try {
            // 获取浏览器指纹
            // const fp = await FingerprintJS.load();
            // const { visitorId } = await fp.get();
            const { utm_source = '' } = query;
            if (utm_source && ['email', 'emarsys'].includes(String(utm_source)?.toLowerCase())) {
                params.entry_name = 'Email';
                params.page_url = window.location.href;
                params.source_channel = 'Website';
            }

            const toRegisterFunc = isNewVersionRegion ? toRegisterNew : toRegister;

            const res: any = await toRegisterFunc(params, '', locale, _source === LOGIN_MAP.ECOFLOW_WEB_COMMUNITY ? FORUM_APPID_MAP[locale] : '');
            const langLocal = langeMap[locale] || 'en_US';
            setLoading(false); // 关闭loading
            if (res.code === '0') {
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'sign_up',
                    page_name: '/register',
                    entry_method: 'Popup_registration with password',
                    entry_type: 'fixed entry',
                    entry_name: localStorage.getItem('from_page_login') || 'direct',
                    entry_position: 'none',
                    source_channel: 'Website',
                    signup_type: 'password',
                    from_page: localStorage.getItem('from_page_login') || 'direct',
                });

                setLocalStorage('rigisterFrom', query?.redirect === '/support/warranty' ? query?.redirect : '');

                const data = langLocal ? { lang: langLocal } : {};
                Object.assign(data, { email: email, scene: 'ACCOUNT_VERIFICATION' });
                // 如果是us站或者ca，直接订阅不需要勾选，其他站点则需要
                if (locale === 'us' || locale === 'ca' || subscribe) {
                    if (_source === LOGIN_MAP.ECOFLOW_WEB_COMMUNITY) {
                        await createSubscribeCommunity({ email: email, source: SUBSCRIBE_MAP[_source as string] }, locale);
                    } else {
                        await createSubscribe({
                            email: email,
                            source: 'WEB_REGISTER',
                            eventTracking: subscribeCookie({
                                entry_name: 'Create EcoFlow Account',
                            }),
                        });
                    }
                }
                if (isNewVersionRegion) {
                    setRecaptchaResponse('');
                    const rptRef: any = recaptchaRef?.current;
                    rptRef?.reset && rptRef?.reset();
                    setFormData({});
                }

                onLogin(res.data, false);
                setStep({ first: false, second: false, third: true });
            } else {
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'sign_up_fail',
                    page_name: '/register',
                    entry_method: 'Popup_registration with password',
                    entry_type: 'fixed entry',
                    entry_name: localStorage.getItem('from_page_login') || 'direct',
                    entry_position: 'none',
                    source_channel: 'Website',
                    signup_type: 'password',
                });
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'login_fail',
                    page_name: '/login',
                });
                if (!isNewVersionRegion) {
                    setRecaptchaResponse('');
                    const rptRef: any = recaptchaRef?.current;
                    rptRef?.reset && rptRef?.reset();
                }
                setErrorTip(res.message || 'error');
                setIsErrorTip(true);
            }
        } catch (e) {
            console.log(e);
            gtm.push({
                event: 'uaEvent',
                event_name: 'sign_up_fail',
                page_name: '/register',
                entry_method: 'Popup_registration with password',
                entry_type: 'fixed entry',
                entry_name: localStorage.getItem('from_page_login') || 'direct',
                entry_position: 'none',
                source_channel: 'Website',
                signup_type: 'password',
            });
            setErrorTip('error');
            setLoading(false); // 关闭loading
        }
    };

    const sendEmailCode = async () => {
        const account = form.getFieldValue('email');
        // const recaptchaRes = humanCheck ? { recaptchaResponse } : {};

        let recaptchaRes = {};
        // register step 1 need recaptcha
        if (step.first) {
            recaptchaRes = { recaptchaResponse };
        }

        // register step 2 need recaptcha if humanCheck is true
        if (step.second) {
            recaptchaRes = humanCheck ? { recaptchaResponse } : {};
        }

        const obj = { scene: 'WEBSITE_REGISTER_VERIFICATION_CODE', account, ...recaptchaRes };
        setLoading(true);
        try {
            const res: any = await getVerifyCode(obj, locale);
            setLoading(false);
            if (res.code === '0') {
                setStep({ first: false, second: true, third: false });
                // step 1 not need HumanCheck
                !!step.second && setHumanCheck(false);
            } else if (res.code === '2017') {
                // step 1 not need HumanCheck
                // step 1 已经传了 recaptchaResponse, 正常情况下不会返回 2017 code
                !!step.second && setHumanCheck(true);
                setIsErrorTip(true);
                setErrorTip(res.message);
            } else {
                setIsErrorTip(true);
                setErrorTip(res.message);
            }
        } catch (error) {
            setLoading(false);
        }
    };

    const goRegister = () => {
        try {
            gtm.push({
                event: 'uaEvent',
                event_name: 'login_register_switch',
                button_name: 'Register without password',
            });
            const searchParams = new URLSearchParams(window.location.search);
            const queryString = searchParams.toString();
            const targetUrl = `/${locale}/register/passwordless${queryString ? `?${queryString}` : ''}`;
            signupCookie({ entry_name: 'Register without password' });

            router.push(targetUrl);
            // window.location.href = `/${locale}/register/passwordless${window.location.search}`;
        } catch (err) {}
    };

    const goLogin = () => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'login_register_switch',
            button_name: isNewVersionRegion ? t('site_sign_instead') : t('site_login_login'),
        });
        // let url = redirect ? redirect : `/${locale}/login`;
        // if (redirect) url += `?redirect=${encodeURIComponent(redirect)}`;

        let url = `/${locale}/login`;
        if (redirect) url += `?redirect=${encodeURIComponent(redirect)}`;
        signupCookie({
            entry_name: 'Create EcoFlow Account',
        });
        router.push(url);
    };

    const onReCAPTCHAChange = async (captchaCode: string) => {
        if (!captchaCode) return;
        setRecaptchaResponse(captchaCode);
        // form.validateFields(['recaptchaResponse']);
        form.setFields([
            {
                name: 'recaptchaResponse',
                value: captchaCode,
                errors: [],
            },
        ]);
    };

    const goAfterLogin = (btnName?: string) => {
        // 如果传入了 onSuccess 回调，则不跳转页面，调用回调
        if (onSuccess) {
            onSuccess();
            return;
        }

        const url = redirect ? redirect : `/${locale}`;
        window.location.href = url;
        // router.push(url);
    };

    const handleFinish = async () => {
        // 处理旧注册场景
        const data = form.getFieldsValue();
        setFormData(data);

        gtm.push({
            event: 'uaEvent',
            event_name: 'click_sign_up',
            button_name: `Popup_${isNewVersionRegion ? t('site_register_next') : t('site_register_submit')}`,
            signup_type: 'password',
            source_channel: 'Website',
            from_page: localStorage.getItem('from_page_login') || 'direct',
        });

        if (isNewVersionRegion) {
            // step 1 not need checkHumanMachine,因为已经传递了recaptchaResponse
            sendEmailCode();
            // handleBeforeSendCode();
        } else {
            await handleSubmit(data);
        }
    };

    const handleBeforeSendCode = async () => {
        if (humanCheck && recaptchaResponse) {
            sendEmailCode();
            return;
        }
        const params = {
            functionName: 'register',
            account: email,
        };
        setLoading(true);
        try {
            const res = await checkHumanMachine(params);
            setLoading(false);
            if (res.code === '0') {
                setHumanCheck(res.data);
                if (!res.data) {
                    sendEmailCode();
                }
            } else {
                setErrorTip(res.message);
                setIsErrorTip(true);
            }
            return;
        } catch (error) {
            setLoading(false);
            console.log(error);
        }
    };

    const validateConfirmPassword = ({ getFieldValue }: any) => {
        return {
            validator(_: any, value: any) {
                const pwd = getFieldValue('password');
                setIsErrorTip(false);
                if (!value) {
                    setAllValidate({
                        ...allValidate,
                        confirmPassword: false,
                    });
                    return Promise.reject(t('site_login_tip_password_empty'));
                }
                if (value && !pwdRegx.test(value)) {
                    return Promise.reject(t('site_login_tip_password_format_wrong'));
                }
                if (pwd !== value) {
                    setAllValidate({
                        ...allValidate,
                        confirmPassword: false,
                    });
                    return Promise.reject(t('site_login_tip_password_twice_inconsistent'));
                }
                setAllValidate({
                    ...allValidate,
                    confirmPassword: true,
                });
                return Promise.resolve();
            },
        };
    };

    useEffect(() => {
        // 埋点，协议勾选
        if (['us', 'ca', 'au', 'jp'].includes(locale)) {
            gtm.push({
                event: 'uaEvent',
                event_name: 'register_privacy_agree',
                tag: true,
            });
        }
    }, [gtm, locale]);

    useEffect(() => {
        const info = getKaamelPlan();
        setKaamelInfo(info);
    }, []);

    return (
        <Modal
            className={`${styled.register} ${step.second ? styled.stepSecond : ''}`}
            visible={visible}
            onClose={onCancel}
            title={t('site_register_register')}
        >
            <div>
                <div className="login-box">
                    <div className={`right ${step.third ? 'third-right' : ''}`}>
                        {step.first && (
                            <div className="register-box">
                                {/* <h1>{t('site_register_register')}</h1> */}
                                <Form name="normal_login" className="login-form" form={form} initialValues={{ remember: isSelected }} onFinish={handleFinish}>
                                    <div className="inputItem">
                                        <label className={inputClass.activeFirst == 'active' ? 'active' : ''}>{t('site_login_email')}</label>
                                        <Form.Item name="email" shouldUpdate validateStatus={emailValidating} rules={[validateEmail]} validateTrigger="onBlur">
                                            <Input
                                                className="default-input "
                                                autoComplete="off"
                                                aria-label={t('site_login_email')}
                                                onFocus={(e: any) => onFocusInput(e, 'email')}
                                                onBlur={(e: any) => onBlurInput(e, 'email')}
                                                onChange={debounce(() => {
                                                    if (!inputClass.activeFirst) {
                                                        SetInputClass({
                                                            activeFirst: 'active',
                                                            activeSecond: inputClass.activeSecond,
                                                            activeThird: inputClass.activeThird,
                                                            name: 'email',
                                                        });
                                                        form.validateFields(['Email']);
                                                    }
                                                }, 300)}
                                            />
                                        </Form.Item>
                                    </div>

                                    <div className="inputItem">
                                        <label className={inputClass.activeSecond == 'active' ? 'active' : ''}>{t('site_login_password')}</label>
                                        <Form.Item name="password" rules={[validatePassword]} validateTrigger="onBlur">
                                            <Input.Password
                                                type="password"
                                                className="default-input"
                                                autoComplete="new-password"
                                                aria-label={t('site_login_password')}
                                                iconRender={(visible: boolean) => <div>{visible ? <EFAkIn className={'iconfont'} /> : <EFAkout />}</div>}
                                                onFocus={(e: any) => onFocusInput(e, 'pwd')}
                                                onBlur={(e: any) => onBlurInput(e, 'pwd')}
                                            />
                                        </Form.Item>
                                        {isShowTip && (
                                            <div className="password-tip">
                                                <div className="header-line">
                                                    <span>Example</span>
                                                    <span>Chage123#4</span>
                                                </div>
                                                <div className="password-tip-content">{t('site_login_tip_password_format_wrong')}</div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="inputItem">
                                        <label className={inputClass.activeThird == 'active' ? 'active' : ''}>{t('site_register_enter_password_again')}</label>
                                        <Form.Item
                                            name="confirmPassword"
                                            shouldUpdate
                                            dependencies={['password']}
                                            rules={[validateConfirmPassword]}
                                            validateTrigger="onBlur"
                                        >
                                            <Input.Password
                                                type="password"
                                                className="default-input"
                                                autoComplete="new-password"
                                                aria-label={t('site_register_enter_password_again')}
                                                iconRender={(visible: boolean) => <div>{visible ? <EFAkIn className={'iconfont'} /> : <EFAkout />}</div>}
                                                onFocus={(e: any) => onFocusInput(e, 'conpwd')}
                                                onBlur={(e: any) => onBlurInput(e, 'conpwd')}
                                            />
                                        </Form.Item>
                                    </div>
                                    <div className="inputItem">
                                        <Form.Item
                                            name="recaptchaResponse"
                                            rules={[
                                                {
                                                    validator() {
                                                        if (recaptchaResponse) {
                                                            return Promise.resolve();
                                                        }
                                                        return Promise.reject(t('site_login_tip_perform_verification'));
                                                    },
                                                },
                                            ]}
                                            style={{ marginBottom: '16px', marginTop: '16px' }}
                                        >
                                            <div className="g-recaptcha">
                                                {/* @ts-ignore */}
                                                <ReCAPTCHA ref={recaptchaRef} hl={hl} sitekey={SECRET_KEY} onChange={onReCAPTCHAChange} />
                                            </div>
                                        </Form.Item>
                                        {isErrorTip && <div className="error-tip">{errorTip}</div>}
                                    </div>
                                    {locale !== 'us' && locale !== 'ca' && (
                                        <Form.Item name="subscribe" valuePropName="checked" noStyle>
                                            <Checkbox className="agreement subscribe">{t('site_register_agree_subscribe')}</Checkbox>
                                        </Form.Item>
                                    )}
                                    <Form.Item style={{ marginBottom: '16px' }}>
                                        <Form.Item
                                            name="remember"
                                            valuePropName="checked"
                                            rules={[
                                                {
                                                    validator: (_, value) =>
                                                        value ? Promise.resolve('') : Promise.reject(t('site_login_tip_agree_user_agreement_error')),
                                                },
                                            ]}
                                            noStyle
                                        >
                                            <Checkbox
                                                className="agreement"
                                                onChange={(e: CheckboxChangeEvent) => {
                                                    // Agree/Disagree/None
                                                    const isAgreed = e?.target?.checked ? 'Agree' : 'Disagree';
                                                    gtm.push({
                                                        event: 'uaEvent',
                                                        event_name: 'register_privacy_agree',
                                                        tag: isAgreed,
                                                    });
                                                }}
                                            >
                                                <span className="checkbox-msg">
                                                    {t.rich('site_login_tip_agree_user_agreement1', {
                                                        link1: (children: any) => (
                                                            <Link
                                                                href={
                                                                    locale === 'cis'
                                                                        ? 'https://account.ecoflow.com/agreement/ru-cis/PrivacyPolicy.html'
                                                                        : locale === 'uk' && kaamelInfo?.privacyUrl
                                                                        ? kaamelInfo?.privacyUrl
                                                                        : `https://account.ecoflow.com/agreement/${
                                                                              i18nMap[locale] || i18nMap.us
                                                                          }/PrivacyPolicy.html`
                                                                }
                                                                newWin
                                                            >
                                                                {children}
                                                            </Link>
                                                        ),
                                                        link2: (children: any) => (
                                                            <Link
                                                                href={
                                                                    locale === 'cis'
                                                                        ? 'https://account.ecoflow.com/agreement/ru-cis/TermsOfUse.html'
                                                                        : `https://account.ecoflow.com/agreement/${
                                                                              i18nMap[locale] || i18nMap.us
                                                                          }/TermsOfUse.html`
                                                                }
                                                                newWin
                                                            >
                                                                {children}
                                                            </Link>
                                                        ),
                                                    })}
                                                </span>
                                            </Checkbox>
                                        </Form.Item>
                                    </Form.Item>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        className="register-form-submit"
                                        loading={loading}
                                        aria-label={t('site_register_submit')}
                                        disabled={!(allValidate.email && allValidate.pwd && allValidate.confirmPassword)}
                                    >
                                        {isNewVersionRegion ? t('site_register_next') : t('site_register_submit')}
                                    </Button>
                                    {/* <div className="navigate-content">
                                        <Button type="link" className="other-button" onClick={() => goLogin()} aria-label={t('site_login_login')}>
                                            {isNewVersionRegion ? t('site_sign_instead') : t('site_login_login')}
                                        </Button>
                                        <Button type="link" className="other-button" onClick={() => goRegister()} aria-label={t('site_login_login')}>
                                            {t('register_btn_text')}
                                        </Button>
                                    </div> */}
                                </Form>
                            </div>
                        )}
                        {step.second && (
                            <div className="register-box box-center second-box">
                                <h1 className="second-title">{t('site_register_second_title')}</h1>
                                <div className="second-desc">
                                    {t.rich('site_register_second_desc', {
                                        a: () => (
                                            <a style={{ color: '#3370ff', textDecoration: 'underline' }} href={`mailto:${formData.email}`}>
                                                {formData.email}
                                            </a>
                                        ),
                                        b: (text: string) => <span style={{ fontFamily: 'var(--font-family-Bold)' }}>{text}</span>,
                                    })}
                                </div>
                                <Form name="code_form" className="login-form" form={emailCodeForm} initialValues={{ remember: false }}>
                                    <div className="inputItem">
                                        <label className={emailCodeInputStatus !== 0 ? 'active' : ''}>{t('site_register_enter_code')}</label>
                                        <Form.Item name="code" shouldUpdate>
                                            <Input
                                                className="default-input "
                                                autoComplete="off"
                                                aria-label={t('site_register_enter_code')}
                                                maxLength={6}
                                                disabled={loading}
                                                onFocus={(e: any) => {
                                                    const val = e.target.value;
                                                    setEmailCodeInputStatus(val ? 2 : 1);
                                                }}
                                                onBlur={(e: any) => {
                                                    const val = e.target.value;
                                                    setEmailCodeInputStatus(val ? 2 : 0);
                                                }}
                                                onChange={debounce((e) => {
                                                    setErrorTip('');
                                                    setIsErrorTip(false);
                                                    const val = e.target.value;
                                                    setEmailCodeInputStatus(val ? 2 : 1);
                                                    if (val.length === 6) {
                                                        handleSubmit();
                                                    }
                                                }, 300)}
                                            />
                                        </Form.Item>
                                        <div className="error-tip">{errorTip}</div>
                                        {isErrorTip && <div className="error-tip">{errorTip}</div>}
                                    </div>
                                    {humanCheck && isNewVersionRegion && (
                                        <Form.Item
                                            name="recaptchaResponse"
                                            rules={[
                                                {
                                                    validator() {
                                                        if (recaptchaResponse) {
                                                            return Promise.resolve();
                                                        }
                                                        return Promise.reject(t('site_login_tip_perform_verification'));
                                                    },
                                                },
                                            ]}
                                            style={{ marginBottom: '32px', marginTop: '12px' }}
                                        >
                                            <div className="g-recaptcha">
                                                {/* @ts-ignore */}
                                                <ReCAPTCHA ref={recaptchaRef} hl={hl} sitekey={SECRET_KEY} onChange={onReCAPTCHAChange} />
                                            </div>
                                        </Form.Item>
                                    )}
                                </Form>
                                <Button
                                    type="link"
                                    disabled={resendCode !== 0}
                                    className="resend-code"
                                    onClick={() => {
                                        gtm.push({
                                            event: 'uaEvent',
                                            event_name: 'resend_verification_coder',
                                            button_name: t('site_resend_code'),
                                        });
                                        handleBeforeSendCode();
                                        setResendCode(60);
                                    }}
                                >
                                    {t('site_resend_code')}
                                    <CountDown
                                        sec={resendCode}
                                        secUnit={t('secondUnit')}
                                        onFinish={() => {
                                            setResendCode(0);
                                        }}
                                    />
                                </Button>
                            </div>
                        )}
                        {step.third && (
                            <div className="register-box box-center">
                                <p>{t('site_register_successful')}</p>
                                <Button
                                    type="primary"
                                    className="black-btn large login-redirect-button"
                                    aria-label={t('site_register_got_it')}
                                    onClick={() => {
                                        goAfterLogin(t('site_register_got_it'));
                                        gtm.push({
                                            event: 'uaEvent',
                                            event_name: 'click_login',
                                            button_name: `Popup_${t('site_register_got_it')}`,
                                            source_channel: 'Website',
                                            from_page: localStorage.getItem('from_page_login') || 'direct',
                                        });
                                    }}
                                >
                                    {t('site_register_got_it')}
                                </Button>
                                <div className="redirect-tip-box">
                                    {t.rich('redirect_tip', {
                                        highLight: (text: string) => (
                                            <span>
                                                {text}
                                                <CountDown
                                                    sec={3}
                                                    isNeedBracket={false}
                                                    secUnit=""
                                                    onFinish={() => {
                                                        goAfterLogin();
                                                    }}
                                                />
                                            </span>
                                        ),
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {step.first && (
                    <>
                        <div className="mode-switch">
                            {t.rich('mode_switch3', {
                                link: (text: string) => (
                                    <span onClick={handleSwitchToRegister} className="mode_switch_link">
                                        {text}
                                    </span>
                                ),
                            })}
                        </div>
                        <div className="divider-line">{`- ${t('site_login_or')} -`}</div>
                        <SocialLoginLinks redirectUrl={redirect} showLineLogin={locale === 'jp'} />
                    </>
                )}
            </div>
        </Modal>
    );
};

export default Register;
